import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于就去色色',
  description: '就去色色是一个专注色彩搭配、视觉美学与创意设计灵感的内容平台。',
  url: 'https://jiuqu.sese/about',
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '就去色色',
  url: 'https://jiuqu.sese',
  description: '专注色彩搭配、视觉美学与创意设计灵感的内容平台，为设计师、摄影师和艺术爱好者提供最全面的色彩资源。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'hello@jiuqu.sese',
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '就去色色是什么平台？',
      acceptedAnswer: { '@type': 'Answer', text: '就去色色是一个专注色彩搭配、视觉美学与创意设计灵感的内容平台，面向设计师、摄影师和艺术爱好者。' },
    },
    {
      '@type': 'Question',
      name: '如何向就去色色投稿？',
      acceptedAnswer: { '@type': 'Answer', text: '您可以通过邮箱 hello@jiuqu.sese 与我们联系，提交您的色彩设计相关文章或案例。' },
    },
    {
      '@type': 'Question',
      name: '就去色色的内容是否免费？',
      acceptedAnswer: { '@type': 'Answer', text: '是的，就去色色所有内容均免费开放阅读。' },
    },
  ],
};

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <SeoHead title="关于我们" description="就去色色是一个专注色彩搭配、视觉美学与创意设计灵感的内容平台。了解我们的品牌故事、核心团队与联系方式。" keywords="就去色色,关于就去色色,色彩美学平台,设计灵感社区" canonicalUrl="/about" jsonLd={{ ...aboutJsonLd, ...orgJsonLd, ...faqJsonLd }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-14 md:pb-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' },{ label: '关于我们' }]} />

            <div className="mt-6 text-center">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900">关于就去色色</h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                一个专注色彩美学与创意设计灵感的内容平台，为每一个热爱色彩的灵魂而生。
              </p>
            </div>

            <section className="mt-14 md:mt-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-900">我们的品牌故事</h2>
                  <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>就去色色诞生于2024年，由一群热爱色彩与设计的设计师和艺术家共同创立。我们相信，色彩不仅是视觉的语言，更是情感的载体、文化的符号和创意的源泉。</p>
                    <p>在这个信息爆炸的时代，真正有深度、有温度的色彩内容却少之又少。于是，我们决定打造一个专注于色彩美学的内容平台——就去色色。从色彩理论的基础知识到前沿的设计趋势，从东方传统色到西方现代配色，我们致力于为设计师、摄影师和艺术爱好者提供最全面、最专业的色彩内容。</p>
                    <p>「就去色色」这个名字源于对色彩的纯粹热爱——去掉一切繁杂，回归色彩本身。我们希望每一位来到这里的用户，都能在色彩的海洋中找到属于自己的灵感与感动。</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img src="https://readdy.ai/api/search-image?query=Warm%20and%20creative%20design%20studio%20with%20color%20swatches%20paint%20brushes%20and%20art%20supplies%20on%20a%20wooden%20table%2C%20soft%20natural%20light%20from%20large%20windows%2C%20minimalist%20cream%20walls%2C%20artistic%20and%20inspiring%20atmosphere%2C%20editorial%20photography%20style%20with%20gentle%20shadows&width=700&height=500&seq=about-brand-2026&orientation=landscape" alt="就去色色品牌工作室" className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </section>

            <section className="mt-16 md:mt-24">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center">我们的数据</h2>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '200+', label: '深度原创文章', icon: 'ri-article-line' },
                  { value: '50+', label: '配色方案分享', icon: 'ri-palette-line' },
                  { value: '30K+', label: '月度活跃读者', icon: 'ri-user-heart-line' },
                  { value: '98%', label: '读者满意度', icon: 'ri-emotion-happy-line' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto bg-primary-100 rounded-xl flex items-center justify-center mb-3">
                      <i className={`${stat.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-heading text-primary-500">{stat.value}</div>
                    <div className="mt-1 text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16 md:mt-24">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center">读者口碑</h2>
              <p className="mt-3 text-sm text-foreground-500 text-center">来自设计师和创意人的真实评价</p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { quote: '就去色色的色彩理论文章让我重新理解了配色逻辑，每篇文章都值得反复阅读。', name: '林设计师', role: 'UI/UX 设计师' },
                  { quote: '作为摄影师，我在就去色色找到了大量关于色彩搭配的灵感，帮助我提升了作品的视觉表现力。', name: '陈摄影师', role: '自由摄影师' },
                  { quote: '平台上的配色方案分享非常实用，我经常在这里寻找项目灵感和色彩参考。', name: '王创意总监', role: '品牌创意总监' },
                ].map((testimonial) => (
                  <div key={testimonial.name} className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                    <div className="flex items-center gap-1 text-accent-400 mb-4">
                      {[...Array(5)].map((_, i) => (<i key={i} className="ri-star-fill text-sm"></i>))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed italic">「{testimonial.quote}」</p>
                    <div className="mt-4 pt-4 border-t border-background-200">
                      <div className="text-sm font-medium text-foreground-900">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16 md:mt-24">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center">联系我们</h2>
              <p className="mt-3 text-sm text-foreground-500 text-center">有任何问题或合作意向，欢迎随时联系我们</p>
              <div className="mt-8 max-w-lg mx-auto bg-background-50 rounded-xl p-8 border border-background-200/60">
                <form action="https://readdy.ai/api/form/d9reb4snlsngrm4lmb6g" method="POST" data-readdy-form="" id="contact-about-form" onSubmit={async (e) => { e.preventDefault(); const form = e.currentTarget; const formData = new FormData(form); const honeypot = (formData.get('website_alt') as string)?.trim(); if (honeypot) { alert('感谢您的留言，我们会尽快回复！'); form.reset(); return; } const params = new URLSearchParams(); formData.forEach((value, key) => { if (key !== 'website_alt') params.append(key, value.toString()); }); try { const res = await fetch(form.action, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() }); const txt = await res.text(); let parsed; try { parsed = JSON.parse(txt); } catch { parsed = null; } if (res.ok && parsed?.code === 'OK') { alert('感谢您的留言，我们会尽快回复！'); form.reset(); } else { const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试'; alert(errMsg); } } catch { alert('提交失败，请稍后重试'); } }}>
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input id="contact-name" name="name" type="text" required className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的姓名" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input id="contact-email" name="email" type="email" required className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的邮箱" />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea id="contact-message" name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none" placeholder="请输入您的留言（500字以内）"></textarea>
                    </div>
                    <div className="honeypot-field">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button type="submit" className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">发送留言</button>
                  </div>
                </form>
              </div>
            </section>

            <section className="mt-16 md:mt-24">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center">常见问题</h2>
              <div className="mt-8 max-w-2xl mx-auto space-y-4">
                {[
                  { q: '就去色色是什么平台？', a: '就去色色是一个专注色彩搭配、视觉美学与创意设计灵感的内容平台，面向设计师、摄影师和艺术爱好者。我们提供色彩理论、配色方案、设计灵感和创意工具等深度内容。' },
                  { q: '如何向就去色色投稿？', a: '您可以通过邮箱 hello@jiuqu.sese 与我们联系，提交您的色彩设计相关文章或案例。我们欢迎所有与色彩美学相关的原创内容。' },
                  { q: '就去色色的内容是否免费？', a: '是的，就去色色所有内容均免费开放阅读。我们致力于让更多人了解和热爱色彩美学。' },
                  { q: '如何在手机上浏览就去色色？', a: '就去色色采用响应式设计，您可以直接通过手机浏览器访问我们的网站，获得流畅的阅读体验。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}